"use client";

import { useEffect } from "react";

import { safeSessionGet, safeSessionSet } from "@/lib/browser-storage";

/**
 * Deployment-skew resilience — belt-and-suspenders to the R2 chunk retention.
 *
 * The edge worker already serves a prior deploy's `/_next/static` chunks from
 * R2 on an Assets 404 (180-day retention), so the common skew case is fixed
 * server-side and Googlebot never sees a broken render. This handles the rare
 * residual cases retention can't: a tab open PAST the 180-day window, or any
 * retention gap. When a chunk genuinely can't load, React/Next throw a
 * ChunkLoadError (or a <script>/<link> resource error); a single hard reload
 * pulls the current deploy's HTML + chunks.
 *
 * Loop-guarded: we reload at most once per cooldown window, so a genuinely
 * broken new deploy degrades to "show the error" instead of spinning forever.
 * Scoped to /_next/static so third-party (analytics/pixel) load failures never
 * trigger a reload. Client-only behavior — a no-op for crawlers (which, thanks
 * to retention, don't hit a chunk 404 in the first place).
 */

const RELOAD_FLAG = "ish:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 30_000;

function looksLikeChunkError(message: string): boolean {
  return (
    /ChunkLoadError/i.test(message) ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /(error|failed to fetch) .*dynamically imported module/i.test(message)
  );
}

function isStaticAssetTarget(target: EventTarget | null): boolean {
  const el = target as (HTMLScriptElement & HTMLLinkElement) | null;
  if (!el || (el.tagName !== "SCRIPT" && el.tagName !== "LINK")) return false;
  const url = el.src || el.href || "";
  return url.includes("/_next/static/");
}

function reloadOnce(): void {
  // safeSessionGet/Set swallow SecurityError when Storage is blocked (private
  // mode / strict cookies). If blocked, last stays 0 and we still reload once —
  // a reload on a broken core chunk beats a permanently broken page.
  const last = Number(safeSessionGet(RELOAD_FLAG) || 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // already reloaded recently → don't loop
  safeSessionSet(RELOAD_FLAG, String(Date.now()));
  window.location.reload();
}

export default function ChunkErrorReloader() {
  useEffect(() => {
    function onError(event: ErrorEvent) {
      if (
        isStaticAssetTarget(event.target) ||
        looksLikeChunkError(event.message || String(event.error ?? ""))
      ) {
        reloadOnce();
      }
    }
    function onRejection(event: PromiseRejectionEvent) {
      const reason = event.reason as { name?: string; message?: string } | undefined;
      const message =
        reason?.name === "ChunkLoadError"
          ? "ChunkLoadError"
          : String(reason?.message ?? reason ?? "");
      if (looksLikeChunkError(message)) reloadOnce();
    }
    // Capture phase: resource-load errors (script/link onerror) do not bubble,
    // so a non-capturing window listener would miss them.
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
